import type { UserManagerSettings } from 'oidc-client-ts';
import type { RuntimeAuthConfig } from './types';

/**
 * Build OIDC configuration from runtime auth config.
 * Used by the client-side AuthProvider.
 */
export function buildOidcConfig(config: RuntimeAuthConfig): UserManagerSettings {
  const authority = `${config.keycloakUrl}/realms/${config.keycloakRealm}`;
  
  return {
    authority,
    client_id: config.keycloakClientId,
    redirect_uri: `${config.appUrl}/callback`,
    post_logout_redirect_uri: `${config.appUrl}/login`,
    
    // Request offline_access for refresh tokens
    scope: 'openid profile email offline_access',
    
    // Response type for authorization code flow with PKCE
    response_type: 'code',
    
    // We handle token refresh manually via API routes to store refresh token in httpOnly cookie
    automaticSilentRenew: false,
    
    // Don't monitor session - we handle expiry ourselves
    monitorSession: false,
  };
}

/**
 * Get server-side auth configuration from environment variables.
 * Used by API routes that need Keycloak endpoints.
 */
export function getServerAuthConfig() {
  const keycloakUrl = process.env.KEYCLOAK_URL || '';
  const keycloakRealm = process.env.KEYCLOAK_REALM || '';
  const keycloakClientId = process.env.KEYCLOAK_CLIENT_ID || '';
  const appUrl = process.env.APP_URL || 'http://localhost:3000';
  
  const authority = `${keycloakUrl}/realms/${keycloakRealm}`;
  
  return {
    keycloakUrl,
    keycloakRealm,
    keycloakClientId,
    appUrl,
    authority,
    tokenEndpoint: `${authority}/protocol/openid-connect/token`,
    logoutEndpoint: `${authority}/protocol/openid-connect/logout`,
  };
}

/**
 * Cookie name for storing refresh token
 */
export const REFRESH_TOKEN_COOKIE = 'km_refresh_token';

/**
 * Cookie options for refresh token (server-side only)
 */
export const REFRESH_TOKEN_COOKIE_OPTIONS = {
  httpOnly: true,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'strict' as const,
  path: '/',
  // Match Keycloak refresh token lifetime (default is usually 30 days)
  maxAge: 60 * 60 * 24 * 30, // 30 days in seconds
};
